import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { Quote } from '@/shared/types'

/**
 * Hook to fetch the current quote for a project
 * Returns the most recent pending quote if any, otherwise the most recent quote
 */
export function useProjectQuote(projectId?: string) {
  return useQuery({
    queryKey: ['quote', projectId],
    queryFn: async () => {
      if (!projectId) return null

      // First try to get a pending quote
      const { data: pendingQuote, error: pendingError } = await supabase
        .from('quotes')
        .select('*')
        .eq('project_id', projectId)
        .eq('status', 'pending')
        .maybeSingle()

      if (pendingError) throw pendingError
      if (pendingQuote) return pendingQuote as Quote

      // If no pending quote, get the most recent one
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as Quote | null
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to accept a quote
 */
export function useAcceptQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (quoteId: string) => {
      const { data, error } = await supabase.rpc('accept_quote', {
        p_quote_id: quoteId,
      })

      if (error) throw error

      const result = data as { success: boolean; error?: string; amount?: number; currency?: string }
      if (!result.success) {
        throw new Error(result.error || 'Failed to accept quote')
      }

      return result
    },
    onSuccess: () => {
      // Invalidate quote queries
      queryClient.invalidateQueries({ queryKey: ['quote'] })
    },
  })
}

/**
 * Hook to reject a quote with a reason
 */
export function useRejectQuote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quoteId, reason }: { quoteId: string; reason: string }) => {
      const { data, error } = await supabase.rpc('reject_quote', {
        p_quote_id: quoteId,
        p_reason: reason,
      })

      if (error) throw error

      const result = data as { success: boolean; error?: string }
      if (!result.success) {
        throw new Error(result.error || 'Failed to reject quote')
      }

      return result
    },
    onSuccess: () => {
      // Invalidate quote queries
      queryClient.invalidateQueries({ queryKey: ['quote'] })
    },
  })
}
